import React, { useState, useEffect, useRef } from 'react';
import { PrazoBadge, SeloUrgencia, ehUrgenciaAberta } from './PrazoSolicitacao';
import { SeloVisualizado } from './SeloVisualizado';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';

// #6354: dificuldade informada ao concluir.
const DIFICULDADES = [
  { value: 'facil', label: 'Fácil' },
  { value: 'media', label: 'Média' },
  { value: 'dificil', label: 'Difícil' },
] as const;
const rotuloDificuldade = (v?: string | null) => DIFICULDADES.find(d => d.value === v)?.label ?? 'não informada';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { nomeExibicaoAnexo } from '../../src/lib/anexosTarefa';
import TaskDescriptionMarkdown from './TaskDescriptionMarkdown';
import {
  X, Edit2, Check, Loader2, Save, Eye, Plus, Trash2, Send, MessageSquare,
  ListTree, Activity, Paperclip, Play, Pause, Square, Clock, Flag, GitBranch,
  Tag, User, Calendar, CalendarClock, TrendingUp, FileText, FileSpreadsheet,
  FileArchive, FileVideo, FileAudio, Image as ImageIcon, File as FileIcon,
  CheckCircle2, ClipboardPaste, Building2, Briefcase, ArrowUpRight,
} from 'lucide-react';
import { Tarefa, Profile, ComentarioTarefa, TarefaTimeEntry, TarefaStatusEvent, SolicitacaoStatus, SolicitacaoTipo, SolicitacaoDepartamento, TarefaPrioridade, Projeto } from '../../types';
import { formatDate, parseLocalDate } from '../../src/lib/utils';
import { SOLICITACAO_TIPO_GRUPOS, EQUIPES_SOLICITACAO, gruposDoDepartamento, tiposDoGrupo, tipoSolicitacaoInfo, DEPARTAMENTO_BADGE_CLASS, isTipoOutro, departamentoTemTipos, etapasDoDepartamento, rotuloEtapa } from './solicitacaoTipos';
import MentionTextarea from './MentionTextarea';
import { ComentarioTexto, corDoUsuario } from '../../src/components/comentarios/ComentarioTexto';
import { comentarioApagado, TEXTO_COMENTARIO_APAGADO } from '../../src/lib/comentarioApagado';
import DatePicker from '../../src/components/DatePicker';
import { daEquipeDaSolicitacao } from './permissoesSolicitacao';
import ProjetoSeletor from './ProjetoSeletor';

// ───────────────────────────── helpers locais (mesmo padrão de duplicação leve
// já usado em SolicitacoesGantt.tsx, pra não acoplar esse componente à TarefasView) ─────

const priorityColor = (p: string) => {
  switch (p) {
    case 'urgencia': return 'text-white bg-rose-600';
    case 'alta': return 'text-error bg-error/10';
    case 'media': return 'text-warning bg-warning/10';
    case 'baixa': return 'text-success bg-success/10';
    default: return 'text-gray-400';
  }
};

const solicitacaoStatusBadge = (s: string) => {
  switch (s) {
    case 'novo': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300';
    case 'em_analise': return 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400';
    case 'em_desenvolvimento': return 'bg-info/10 text-info';
    case 'em_teste': return 'bg-warning/10 text-warning';
    case 'concluido': return 'bg-success/10 text-success';
    case 'rejeitado': return 'bg-error/10 text-error';
    default: return 'bg-gray-100 text-gray-500';
  }
};

const getDueDateBadgeStyle = (dateStr?: string | null) => {
  if (!dateStr) return 'text-gray-400 dark:text-gray-500';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? parseLocalDate(dateStr) : new Date(dateStr);
  dueDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 px-2 py-0.5 rounded-full';
  if (diffDays === 0) return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 px-2 py-0.5 rounded-full';
  if (diffDays <= 2) return 'text-yellow-600 dark:text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 px-2 py-0.5 rounded-full';
  return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded-full';
};

const formatDuration = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const FILE_ICON_MAP: Record<string, { icon: React.ElementType; bg: string; text: string }> = {
  pdf: { icon: FileText, bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-500' },
  doc: { icon: FileText, bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-500' },
  docx: { icon: FileText, bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-500' },
  txt: { icon: FileText, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500' },
  xls: { icon: FileSpreadsheet, bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600' },
  xlsx: { icon: FileSpreadsheet, bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600' },
  csv: { icon: FileSpreadsheet, bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600' },
  zip: { icon: FileArchive, bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600' },
  rar: { icon: FileArchive, bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600' },
  png: { icon: ImageIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500' },
  jpg: { icon: ImageIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500' },
  jpeg: { icon: ImageIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500' },
  gif: { icon: ImageIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500' },
  webp: { icon: ImageIcon, bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500' },
  mp4: { icon: FileVideo, bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-500' },
  mov: { icon: FileVideo, bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-500' },
  mp3: { icon: FileAudio, bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-500' },
  wav: { icon: FileAudio, bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-500' },
};
const getFileIcon = (url: string) => {
  const ext = (url.split('.').pop() || '').split('?')[0].toLowerCase();
  return FILE_ICON_MAP[ext] || { icon: FileIcon, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500' };
};
// Nome legível do anexo: regra única em src/lib/anexosTarefa.ts, que aceita o
// formato novo (<ts>-<rand>_nome) e o antigo (<ts>_nome).
const getAnexoDisplayName = nomeExibicaoAnexo;

// #6339: comentário aceita qualquer arquivo. Imagem continua em miniatura; o resto vira cartão.
const EXT_IMAGEM = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
const ehImagemUrl = (url: string) => EXT_IMAGEM.has((url.split('.').pop() || '').split('?')[0].toLowerCase());
/** Limite padrão de upload do Supabase Storage por arquivo. */
const LIMITE_ANEXO_COMENTARIO_MB = 50;

const MetaRow: React.FC<{ icon: React.ElementType; label: string; children: React.ReactNode }> = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="flex items-center gap-1.5 w-32 shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500 pt-1.5">
      <Icon size={13} />
      {label}
    </div>
    <div className="flex-1 min-w-0 pt-0.5">{children}</div>
  </div>
);

// ───────────────────────────── componente ─────────────────────────────

interface SolicitacaoDetailModalProps {
  task: Tarefa;
  allTarefas: Tarefa[];
  users: Profile[];
  currentUserId?: string;
  isCreatorOrAdmin: boolean;
  canDirecionarSolicitacoes: boolean;
  canSelfAssignSolicitacao: boolean;

  comments: ComentarioTarefa[];
  taskTimeEntries: TarefaTimeEntry[];
  activityEvents: TarefaStatusEvent[];
  openTimeEntry: TarefaTimeEntry | null;
  timerBusy: boolean;
  timerElapsedSeconds: number;

  onClose: () => void;
  onOpenSubtask: (subtask: Tarefa) => void;
  onStatusChange: (status: SolicitacaoStatus) => void;
  onTipoChange: (tipo: SolicitacaoTipo | null) => void;
  onTipoOutroDetalheChange: (detalhe: string) => void;
  onDepartamentoChange: (departamento: SolicitacaoDepartamento | null) => void;
  onBloqueadaPorPessoaChange: (pessoaId: string | null) => void;
  onBloqueadaPorTextoChange: (texto: string) => void;
  onDefinirDependencia: (pessoaId: string | null, texto: string | null) => void;
  onSolicitanteChange: (solicitanteId: string | null) => void;
  /** Projetos disponíveis para vínculo (módulo Projetos). */
  projetos: Projeto[];
  /** Só adm/gerente vincula; para os demais o campo é leitura. */
  podeVincularProjeto: boolean;
  onProjetoChange: (projetoId: number | null) => void;
  onSaveTitle: (titulo: string) => Promise<void>;
  onSaveDescription: (descricao: string) => Promise<void>;
  onSelfAssign: () => void;
  onDirecionar: () => void;
  onReagendar: (date: string) => Promise<void>;
  onRequestDelete: () => void;
  onStartTimer: () => void;
  onPauseTimer: () => void;
  onFinishTimer: () => void;
  onAddManualTime: (minutes: number, date: string) => Promise<void>;
  onEditTimeEntry: (entryId: number, minutes: number) => Promise<void>;
  onAddAttachment: (file: File) => Promise<void>;
  onRemoveAttachment: (url: string) => void;
  onAddSubtask: (data: { titulo: string; responsavel_id: string; prioridade: TarefaPrioridade; vencimento: string }) => Promise<void>;
  onSendComment: (text: string, mentionedUserIds: string[], files?: File[]) => Promise<void>;
  onEditComment: (commentId: number, text: string) => Promise<void>;
  onDeleteComment: (commentId: number) => Promise<void>;
}

const SolicitacaoDetailModal: React.FC<SolicitacaoDetailModalProps> = ({
  task, allTarefas, users, currentUserId, isCreatorOrAdmin, canDirecionarSolicitacoes, canSelfAssignSolicitacao,
  comments, taskTimeEntries, activityEvents, openTimeEntry, timerBusy, timerElapsedSeconds,
  onClose, onOpenSubtask, onStatusChange, onTipoChange, onTipoOutroDetalheChange, onDepartamentoChange, onBloqueadaPorPessoaChange, onBloqueadaPorTextoChange, onDefinirDependencia, onSolicitanteChange, projetos, podeVincularProjeto, onProjetoChange, onSaveTitle, onSaveDescription, onSelfAssign, onDirecionar, onReagendar,
  onRequestDelete, onStartTimer, onPauseTimer, onFinishTimer, onAddManualTime, onEditTimeEntry, onAddAttachment, onRemoveAttachment, onAddSubtask,
  onSendComment, onEditComment, onDeleteComment,
}) => {
  const [activeTab, setActiveTab] = useState<'subtarefas' | 'comentarios' | 'atividades' | 'dependencia'>('comentarios');

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(task.titulo);
  const [savingTitle, setSavingTitle] = useState(false);

  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(task.descricao || '');
  const [descPreview, setDescPreview] = useState<'edit' | 'preview'>('edit');
  const [savingDesc, setSavingDesc] = useState(false);

  const [uploadingFile, setUploadingFile] = useState(false);

  // Tempo manual (estimativa sem cronômetro) e edição de duração de entradas já fechadas
  const [addingManualTime, setAddingManualTime] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [savingManualTime, setSavingManualTime] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editEntryHours, setEditEntryHours] = useState('');
  const [editEntryMinutes, setEditEntryMinutes] = useState('');
  const [savingEntryEdit, setSavingEntryEdit] = useState(false);

  const [addingSubtask, setAddingSubtask] = useState(false);
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [newSubtask, setNewSubtask] = useState({ titulo: '', responsavel_id: '', prioridade: 'media' as TarefaPrioridade, vencimento: '' });

  const [editingSolicitante, setEditingSolicitante] = useState(false);
  const [outroDetalheValue, setOutroDetalheValue] = useState(task.tipo_outro_detalhe || '');
  const [bloqueadaPorTextoValue, setBloqueadaPorTextoValue] = useState(task.bloqueada_por_texto || '');

  const [newComment, setNewComment] = useState('');
  const [commentMentions, setCommentMentions] = useState<Set<string>>(new Set());
  // Imagens do comentário em composição: colar (Ctrl+V) ou escolher arquivo. Só vão pro
  // storage no envio (handleSubmitComment) — enquanto isso ficam como File + objectURL local.
  const [commentImages, setCommentImages] = useState<{ file: File; preview: string }[]>([]);
  const [commentAnexoErro, setCommentAnexoErro] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentValue, setEditCommentValue] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  // #6287: confirmação inline antes de apagar o próprio comentário.
  const [confirmandoApagarId, setConfirmandoApagarId] = useState<number | null>(null);
  const [apagandoComentario, setApagandoComentario] = useState(false);

  // Concluir tarefa — pede detalhamento (obrigatório), confirmação do tempo gasto (pré-preenchido
  // com o que o cronômetro registrou, ou zerado se nunca foi usado) e print (opcional, com suporte
  // a colar direto da área de transferência) antes de de fato mudar o status pra "concluido".
  // Disparado tanto pelo botão "Concluir tarefa" quanto pelo select de Status ao escolher "Concluído".
  const [showConcludeModal, setShowConcludeModal] = useState(false);
  const [conclusaoDetalhe, setConclusaoDetalhe] = useState('');
  const { addToast } = useToast();
  const [conclusaoDificuldade, setConclusaoDificuldade] = useState<string>('');
  // Histórico do "Depende de" (#6358), gravado pelo banco em toda mudança.
  const [eventosDependencia, setEventosDependencia] = useState<Array<{ id: number; user_id: string | null; acao: 'adicionou' | 'trocou' | 'resolveu'; pessoa_antes: string | null; pessoa_depois: string | null; texto_antes: string | null; texto_depois: string | null; created_at: string }>>([]);
  useEffect(() => {
    let vivo = true;
    supabase.from('tarefa_dependencia_eventos').select('*').eq('tarefa_id', task.id).order('created_at', { ascending: true })
      .then(({ data }) => { if (vivo) setEventosDependencia((data ?? []) as any); });
    return () => { vivo = false; };
  }, [task.id, task.bloqueada_por_pessoa_id, task.bloqueada_por_texto]);
  const [dificuldadeAtual, setDificuldadeAtual] = useState<string | null>(task.dificuldade ?? null);
  useEffect(() => { setDificuldadeAtual(task.dificuldade ?? null); }, [task.id, task.dificuldade]);
  const [conclusaoHoras, setConclusaoHoras] = useState('');
  const [conclusaoMinutos, setConclusaoMinutos] = useState('');
  const [conclusaoPrint, setConclusaoPrint] = useState<File | null>(null);
  const [conclusaoPrintPreview, setConclusaoPrintPreview] = useState<string | null>(null);
  const [savingConclusao, setSavingConclusao] = useState(false);

  const getResponsibleName = (id?: string | null) => {
    if (!id) return 'Não atribuído';
    const u = users.find(u => u.id === id);
    return u ? u.full_name : 'Desconhecido';
  };
  const getUser = (id?: string | null) => users.find(u => u.id === id);

  const Avatar: React.FC<{ userId?: string | null; size?: number }> = ({ userId, size = 22 }) => {
    const u = getUser(userId);
    const name = u?.full_name || 'Não atribuído';
    if (u?.avatar_url) {
      return <img src={u.avatar_url} alt={name} className="rounded-full object-cover border border-gray-200 dark:border-gray-600 shrink-0" style={{ width: size, height: size }} />;
    }
    return (
      <div className="rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-bold text-gray-500 dark:text-gray-400 shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>
        {name.charAt(0).toUpperCase()}
      </div>
    );
  };

  const subtarefas = allTarefas.filter(t => t.parent_id != null && String(t.parent_id) === String(task.id));
  const subtarefasDone = subtarefas.filter(t => (t.solicitacao_status || 'novo') === 'concluido').length;
  const progressPct = subtarefas.length > 0 ? Math.round((subtarefasDone / subtarefas.length) * 100) : 0;

  const isRunningHere = !!openTimeEntry && String(openTimeEntry.tarefa_id) === String(task.id);
  const totalTrackedSeconds = taskTimeEntries.reduce((sum, e) => sum + (e.duration_seconds || 0), 0);

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) return;
    setSavingTitle(true);
    try {
      await onSaveTitle(titleValue.trim());
      setEditingTitle(false);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleSaveDesc = async () => {
    setSavingDesc(true);
    try {
      await onSaveDescription(descValue.trim());
      setEditingDesc(false);
    } finally {
      setSavingDesc(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingFile(true);
    try {
      await onAddAttachment(file);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmitManualTime = async () => {
    const totalMinutes = (parseInt(manualHours, 10) || 0) * 60 + (parseInt(manualMinutes, 10) || 0);
    if (totalMinutes <= 0 || !manualDate) return;
    setSavingManualTime(true);
    try {
      await onAddManualTime(totalMinutes, manualDate);
      setManualHours('');
      setManualMinutes('');
      setManualDate(new Date().toISOString().slice(0, 10));
      setAddingManualTime(false);
    } finally {
      setSavingManualTime(false);
    }
  };

  const startEditEntry = (entry: TarefaTimeEntry) => {
    const total = entry.duration_seconds || 0;
    setEditEntryHours(String(Math.floor(total / 3600)));
    setEditEntryMinutes(String(Math.floor((total % 3600) / 60)));
    setEditingEntryId(entry.id);
  };

  const handleSubmitEntryEdit = async () => {
    if (!editingEntryId) return;
    const totalMinutes = (parseInt(editEntryHours, 10) || 0) * 60 + (parseInt(editEntryMinutes, 10) || 0);
    if (totalMinutes <= 0) return;
    setSavingEntryEdit(true);
    try {
      await onEditTimeEntry(editingEntryId, totalMinutes);
      setEditingEntryId(null);
    } finally {
      setSavingEntryEdit(false);
    }
  };

  const handleSubmitSubtask = async () => {
    if (!newSubtask.titulo.trim()) return;
    setSavingSubtask(true);
    try {
      await onAddSubtask(newSubtask);
      setNewSubtask({ titulo: '', responsavel_id: '', prioridade: 'media', vencimento: '' });
      setAddingSubtask(false);
    } finally {
      setSavingSubtask(false);
    }
  };

  const addCommentImages = (files: File[]) => {
    const grandes = files.filter(f => f.size > LIMITE_ANEXO_COMENTARIO_MB * 1024 * 1024);
    const aceitos = files.filter(f => f.size <= LIMITE_ANEXO_COMENTARIO_MB * 1024 * 1024);
    setCommentAnexoErro(grandes.length
      ? `${grandes.map(f => f.name).join(', ')}: acima de ${LIMITE_ANEXO_COMENTARIO_MB} MB, não dá para anexar.`
      : '');
    if (aceitos.length === 0) return;
    // Só imagem ganha miniatura; os outros arquivos aparecem como cartão com o nome.
    setCommentImages(prev => [...prev, ...aceitos.map(file => ({ file, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '' }))]);
  };

  const removeCommentImage = (index: number) => {
    setCommentImages(prev => {
      const alvo = prev[index];
      if (alvo?.preview) URL.revokeObjectURL(alvo.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCommentImageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    addCommentImages(files);
  };

  // Print copiado com Win+Shift+S entra direto no comentário, sem passar por arquivo em disco.
  const handleCommentPaste = (e: React.ClipboardEvent) => {
    const imagens = Array.from(e.clipboardData.items)
      .filter(i => i.kind === 'file')
      .map(i => i.getAsFile())
      .filter((f): f is File => !!f);
    if (imagens.length === 0) return;
    e.preventDefault();
    addCommentImages(imagens);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    // Comentário só com imagem é válido; só o par vazio (sem texto e sem imagem) é bloqueado.
    if (!newComment.trim() && commentImages.length === 0) return;
    setSendingComment(true);
    try {
      await onSendComment(newComment, Array.from(commentMentions), commentImages.map(i => i.file));
      setNewComment('');
      setCommentMentions(new Set());
      setCommentImages(prev => { prev.forEach(i => URL.revokeObjectURL(i.preview)); return []; });
    } finally {
      setSendingComment(false);
    }
  };

  const handleApagarComentario = async (commentId: number) => {
    setApagandoComentario(true);
    try {
      await onDeleteComment(commentId);
      setConfirmandoApagarId(null);
    } catch {
      // o toast já explicou
    } finally {
      setApagandoComentario(false);
    }
  };

  const handleSubmitCommentEdit = async () => {
    if (!editingCommentId || !editCommentValue.trim()) return;
    setSavingComment(true);
    try {
      await onEditComment(editingCommentId, editCommentValue.trim());
      setEditingCommentId(null);
    } finally {
      setSavingComment(false);
    }
  };

  const setConclusaoFile = (file: File | null) => {
    setConclusaoPrintPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
    setConclusaoPrint(file);
  };

  const handleConclusaoFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (file) setConclusaoFile(file);
  };

  // Cola direto da área de transferência (print copiado com Win+Shift+S, por ex.) — sem precisar
  // salvar em disco e procurar pelo explorer do SO.
  const handleConclusaoPaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    setConclusaoFile(file);
  };

  // Abre o modal de conclusão já com o tempo gasto pré-preenchido a partir do que o cronômetro
  // registrou (zerado se a tarefa nunca teve cronômetro rodado) — chamado tanto pelo botão
  // "Concluir tarefa" quanto pelo select de Status ao escolher "Concluído".
  const openConcludeModal = () => {
    const trackedMinutes = Math.round(totalTrackedSeconds / 60);
    setConclusaoDificuldade(dificuldadeAtual ?? '');
    setConclusaoHoras(String(Math.floor(trackedMinutes / 60)));
    setConclusaoMinutos(String(trackedMinutes % 60));
    setShowConcludeModal(true);
  };

  const closeConcludeModal = () => {
    setShowConcludeModal(false);
    setConclusaoDetalhe('');
    setConclusaoDificuldade('');
    setConclusaoHoras('');
    setConclusaoMinutos('');
    setConclusaoFile(null);
  };

  const handleSubmitConclusao = async () => {
    if (!conclusaoDetalhe.trim()) return;
    if (pedeDificuldade && !conclusaoDificuldade) return;
    setSavingConclusao(true);
    try {
      if (conclusaoPrint) {
        await onAddAttachment(conclusaoPrint);
      }
      // Tempo gasto: só registra manualmente a diferença pra cima (ex.: cronômetro nunca rodou,
      // ou rodou mas ficou aquém do real). Reduzir o total exige editar/apagar entradas já
      // existentes, o que já tem UI própria na seção Cronômetro — não duplicamos isso aqui.
      const confirmedMinutes = fluxoTI ? (parseInt(conclusaoHoras, 10) || 0) * 60 + (parseInt(conclusaoMinutos, 10) || 0) : 0;
      const trackedMinutes = Math.round(totalTrackedSeconds / 60);
      if (confirmedMinutes > trackedMinutes) {
        await onAddManualTime(confirmedMinutes - trackedMinutes, new Date().toISOString().slice(0, 10));
      }
      if (pedeDificuldade) {
        // .select('id'): sem linha devolvida o banco recusou em silêncio (permissão) e a conclusão seguiria sem a dificuldade.
        const { data: gravada, error } = await supabase.from('tarefas').update({ dificuldade: conclusaoDificuldade }).eq('id', task.id).select('id');
        if (error || !gravada?.length) throw error ?? new Error('sem permissão para gravar a dificuldade');
        setDificuldadeAtual(conclusaoDificuldade);
      }
      await onSendComment(`✅ Conclusão: ${conclusaoDetalhe.trim()}${pedeDificuldade ? `

Dificuldade: ${rotuloDificuldade(conclusaoDificuldade)}` : ''}`, []);
      await onStatusChange('concluido');
      closeConcludeModal();
    } catch (err: any) {
      console.error('Erro ao concluir a solicitação:', err);
      addToast('Não foi possível concluir a solicitação. Tente de novo e, se repetir, avise o suporte.', 'error');
    } finally {
      setSavingConclusao(false);
    }
  };

  const tipoInfo = tipoSolicitacaoInfo(task.tipo_solicitacao);
  // Reclassificar é ato de triagem — mesma turma que pode Direcionar (TAR-R11). Quem não pode
  // continua vendo o badge estático. (O select de Status ao lado é ungated de propósito; aqui
  // restringimos porque o tipo é o que alimenta os relatórios da fila.)
  const podeEditarTipo = isCreatorOrAdmin || canDirecionarSolicitacoes;
  const semTipoClassName = 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300';

  // Projeto vinculado, só se a pessoa enxerga ele (a lista vem sob RLS) — senão não há para
  // onde levar. Subtarefa não carrega projeto_id próprio, então aqui fica null para ela.
  const projetoAtual = task.projeto_id != null ? projetos.find(p => p.id === task.projeto_id) ?? null : null;
  // O modal é global e abre por cima de qualquer tela — navegar sem fechar deixaria ele em cima
  // do projeto. Ctrl/Cmd/Shift/botão do meio abrem em outra aba: aí o modal fica onde está.
  const aoIrParaProjeto = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    onClose();
  };

  // Dependência: bloqueada por uma pessoa + motivo — nenhum dos dois tem "status" pra checar
  // resolução automática, então conta como bloqueio ativo enquanto qualquer um estiver preenchido.
  const aindaBloqueando = !!task.bloqueada_por_pessoa_id || !!task.bloqueada_por_texto;
  // #6358: o responsável também resolve/recoloca a dependência, não só quem triagem.
  const podeMexerDependencia = podeEditarTipo || currentUserId === task.responsavel_id;
  const ultimaResolvida = [...eventosDependencia].reverse().find(e => e.acao === 'resolveu') ?? null;
  // #6354: dificuldade é obrigatória ao concluir — menos quando ainda depende de alguém.
  // Fluxo da TI (Tecnologia/Marketing ou solicitação antiga sem equipe): cronômetro, tempo e dificuldade.
  // As outras equipes (#6518) concluem só com o "O que foi feito?".
  const fluxoTI = !task.departamento_solicitacao || departamentoTemTipos(task.departamento_solicitacao);
  const pedeDificuldade = !aindaBloqueando && fluxoTI;

  // Checklist dos pedidos por tarefa (#6518). Quem pode mexer: quem pediu, o responsável e adm/gerentes.
  const podeEditarChecklist = isCreatorOrAdmin || currentUserId === task.responsavel_id;
  const [checklist, setChecklist] = useState<{ id: string; text: string; completed: boolean }[]>(task.checklists || []);
  const [novoItemChecklist, setNovoItemChecklist] = useState('');
  useEffect(() => { setChecklist(task.checklists || []); }, [task.id, task.checklists]);
  const salvarChecklist = async (nova: { id: string; text: string; completed: boolean }[]) => {
    const anterior = checklist;
    setChecklist(nova);
    const { data, error } = await supabase.from('tarefas').update({ checklists: nova }).eq('id', task.id).select('id');
    if (error || !data?.length) {
      setChecklist(anterior);
      if (error) console.error('Erro ao salvar a checklist:', error);
      addToast('Não foi possível salvar a checklist. Tente de novo.', 'error');
    }
  };
  const podeMudarDificuldade = isCreatorOrAdmin || currentUserId === task.responsavel_id;
  // Escolher no select só marca a opção; gravar (e comentar/notificar) é no "Salvar". Com a gravação
  // no onChange, quem navega pelo teclado (setas num select fechado) disparava um comentário por tecla.
  const [dificuldadePendente, setDificuldadePendente] = useState<string | null>(null);
  const [salvandoDificuldade, setSalvandoDificuldade] = useState(false);
  useEffect(() => { setDificuldadePendente(null); }, [task.id]);
  const mudarDificuldade = async (nova: string) => {
    const antiga = dificuldadeAtual;
    if (!nova || nova === antiga) { setDificuldadePendente(null); return; }
    setSalvandoDificuldade(true);
    const { data: gravada, error } = await supabase.from('tarefas').update({ dificuldade: nova }).eq('id', task.id).select('id');
    setSalvandoDificuldade(false);
    if (error || !gravada?.length) {
      if (error) console.error('Erro ao mudar a dificuldade:', error);
      addToast('Não foi possível mudar a dificuldade. Confira se você pode editar esta solicitação e tente de novo.', 'error');
      return;
    }
    setDificuldadePendente(null);
    addToast('Dificuldade atualizada.', 'success');
    setDificuldadeAtual(nova);
    const quem = users.find(u => u.id === currentUserId)?.full_name || 'Alguém';
    await onSendComment(`🔧 ${quem} mudou a dificuldade de ${rotuloDificuldade(antiga)} para ${rotuloDificuldade(nova)}.`, []);
    // Quem concluiu (o responsável) fica sabendo quando outra pessoa muda.
    if (task.responsavel_id && task.responsavel_id !== currentUserId) {
      await supabase.from('notifications').insert({
        user_id: task.responsavel_id,
        type: 'solicitacao_dificuldade',
        title: 'Dificuldade alterada',
        content: `${quem} mudou a dificuldade de "${task.titulo}" de ${rotuloDificuldade(antiga)} para ${rotuloDificuldade(nova)}.`,
        link: `/tarefas/solicitacoes?id=${task.id}`,
        metadata: { tarefa_id: task.id },
      });
    }
  };

  // Aba padrão ao abrir/trocar de solicitação, em ordem de prioridade: (1) Dependência, se
  // houver bloqueio ativo — é o que mais precisa de atenção; (2) Subtarefas, se esta não for
  // ela mesma uma subtarefa e tiver subtarefas dentro; (3) Comentários, foco padrão no resto
  // dos casos (inclusive quando é a própria subtarefa sendo aberta).
  useEffect(() => {
    if (aindaBloqueando) {
      setActiveTab('dependencia');
    } else if (!task.parent_id && subtarefas.length > 0) {
      setActiveTab('subtarefas');
    } else {
      setActiveTab('comentarios');
    }
  }, [task.id]);
  // As subtarefas chegam depois de a janela abrir (busca à parte): quando chegam, troca para a aba
  // delas uma vez só — sem isso a solicitação mãe abria sempre em Comentários.
  const abaDasSubtarefasAplicadaRef = useRef<string | null>(null);
  useEffect(() => {
    if (subtarefas.length === 0 || task.parent_id || aindaBloqueando) return;
    if (abaDasSubtarefasAplicadaRef.current === String(task.id)) return;
    abaDasSubtarefasAplicadaRef.current = String(task.id);
    setActiveTab('subtarefas');
  }, [task.id, subtarefas.length]);

  // Sugestão de @menção prioriza a equipe da solicitação (#6518) + solicitante/responsável —
  // sem isso a lista mostrava a empresa inteira sem nenhuma ordem relevante pro contexto.
  const priorityUserIds = users
    .filter(u => (task.departamento_solicitacao && daEquipeDaSolicitacao(task, u)) || u.id === task.solicitante_id || u.id === task.responsavel_id)
    .map(u => u.id);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 md:p-6 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-7xl shadow-2xl overflow-hidden flex flex-col h-[96vh] md:h-[92vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start bg-gray-50 dark:bg-gray-900/50 shrink-0">
          <div className="flex-1 mr-4 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                Solicitação
              </span>
              {task.parent_id && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  Subtarefa
                </span>
              )}
              {/* Caminho de volta para o projeto, no alto — é o primeiro lugar onde se procura
                  "de onde é isto". O campo Projeto, mais abaixo, é para trocar o vínculo. */}
              {projetoAtual && (
                <Link
                  to={`/projetos/${projetoAtual.id}`}
                  onClick={aoIrParaProjeto}
                  title="Ir para o projeto"
                  className="group/proj inline-flex items-center gap-1.5 min-w-0 max-w-full text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary-light transition-colors"
                >
                  <span className="text-gray-300 dark:text-gray-600">/</span>
                  <Briefcase size={12} className="shrink-0" />
                  <span className="truncate group-hover/proj:underline underline-offset-2">{projetoAtual.nome}</span>
                  <ArrowUpRight size={12} className="shrink-0 opacity-60 group-hover/proj:opacity-100" />
                </Link>
              )}
            </div>
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={titleValue}
                  onChange={e => setTitleValue(e.target.value)}
                  className="text-2xl font-bold bg-white dark:bg-gray-800 border border-primary text-gray-900 dark:text-white rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                  onKeyDown={e => e.key === 'Enter' && handleSaveTitle()}
                  autoFocus
                />
                <button onClick={handleSaveTitle} disabled={savingTitle} className="p-1.5 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors shrink-0">
                  {savingTitle ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                </button>
                <button onClick={() => { setEditingTitle(false); setTitleValue(task.titulo); }} className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors shrink-0">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{task.titulo}</h2>
                {isCreatorOrAdmin && (
                  <button onClick={() => { setTitleValue(task.titulo); setEditingTitle(true); }} className="p-1 text-gray-400 hover:text-primary transition-all opacity-0 group-hover:opacity-100 shrink-0">
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(task.solicitacao_status || 'novo') !== 'concluido' && (
              <button
                onClick={openConcludeModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-success text-white rounded-lg text-sm font-semibold hover:bg-success/90 transition-colors"
              >
                <CheckCircle2 size={16} /> Concluir tarefa
              </button>
            )}
            {/* Sem gate de isCreatorOrAdmin aqui de propósito — mesmo nível de permissão do "Excluir"
                já disponível no dropdown do card (Kanban/Tabela/Lista), que também não restringe. */}
            <button onClick={onRequestDelete} title="Excluir" className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
              <Trash2 size={18} />
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Corpo — coluna principal (metadados/cronômetro/descrição/anexos) + rail lateral fixo
            à direita (subtarefas/comentários/atividades). Em telas grandes cada coluna rola de
            forma independente; em mobile as duas empilham e rolam junto com o modal. */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden min-h-0">
        <div className="flex-1 lg:overflow-y-auto p-5 md:p-6 space-y-6 min-w-0">

          {/* Metadados */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
            {/* Departamento decide qual vocabulário de Tipo vale — fica ao lado dele de propósito */}
            <MetaRow icon={Building2} label="Equipe">
              {podeEditarTipo ? (
                <select
                  aria-label="Equipe"
                  value={task.departamento_solicitacao || ''}
                  onChange={e => onDepartamentoChange((e.target.value || null) as SolicitacaoDepartamento | null)}
                  className={`text-xs font-bold uppercase px-2 py-1 rounded-lg border-0 outline-none cursor-pointer max-w-full ${task.departamento_solicitacao ? DEPARTAMENTO_BADGE_CLASS[task.departamento_solicitacao] : semTipoClassName}`}
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">Sem equipe</option>
                  {EQUIPES_SOLICITACAO.map(eq => eq.departamentos.length > 1 ? (
                    <optgroup key={eq.id} label={eq.label} className="bg-white dark:bg-gray-800">
                      {eq.departamentos.map(dep => (
                        <option key={dep} value={dep} className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">{dep}</option>
                      ))}
                    </optgroup>
                  ) : (
                    <option key={eq.id} value={eq.departamentos[0]} className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">{eq.label}</option>
                  ))}
                </select>
              ) : (
                <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase inline-block ${task.departamento_solicitacao ? DEPARTAMENTO_BADGE_CLASS[task.departamento_solicitacao] : semTipoClassName}`}>
                  {task.departamento_solicitacao || 'Sem equipe'}
                </span>
              )}
            </MetaRow>
            {/* Projeto ao qual a solicitação pertence (módulo Projetos). Só subtarefa fica de
                fora: ela pertence ao projeto pela mãe, não por vínculo próprio. */}
            {task.parent_id == null && (
              <MetaRow icon={Briefcase} label="Projeto">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="min-w-0">
                    <ProjetoSeletor
                      projetos={projetos}
                      value={task.projeto_id}
                      onChange={onProjetoChange}
                      somenteLeitura={!podeVincularProjeto}
                    />
                  </div>
                  {projetoAtual && (
                    <Link
                      to={`/projetos/${projetoAtual.id}`}
                      onClick={aoIrParaProjeto}
                      className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary dark:text-primary-light hover:underline shrink-0"
                    >
                      Abrir <ArrowUpRight size={12} />
                    </Link>
                  )}
                </div>
              </MetaRow>
            )}
            {/* Linha de Tipo só para Tecnologia e Marketing (ou sem equipe, caso das antigas, que precisam
                poder ganhar um); as outras equipes não têm tipo (#6518). */}
            {(!task.departamento_solicitacao || departamentoTemTipos(task.departamento_solicitacao)) && (
            <MetaRow icon={Tag} label="Tipo">
              {podeEditarTipo ? (
                <select
                  value={task.tipo_solicitacao || ''}
                  onChange={e => onTipoChange((e.target.value || null) as SolicitacaoTipo | null)}
                  className={`text-xs font-bold uppercase px-2 py-1 rounded-lg border-0 outline-none cursor-pointer max-w-full ${tipoInfo?.className || semTipoClassName}`}
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">Sem tipo</option>
                  {(task.departamento_solicitacao ? gruposDoDepartamento(task.departamento_solicitacao) : SOLICITACAO_TIPO_GRUPOS).map(grupo => (
                    <optgroup key={grupo} label={grupo} className="bg-white dark:bg-gray-800">
                      {tiposDoGrupo(grupo, task.departamento_solicitacao || undefined).map(t => (
                        <option key={t.value} value={t.value} title={t.descricao} className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">{t.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              ) : (
                <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase inline-block ${tipoInfo?.className || semTipoClassName}`}>
                  {tipoInfo?.label || 'Sem tipo'}
                </span>
              )}
            </MetaRow>
            )}
            {isTipoOutro(task.tipo_solicitacao) && (
              <MetaRow icon={Tag} label="Qual o tipo?">
                {podeEditarTipo ? (
                  <input
                    type="text"
                    maxLength={140}
                    value={outroDetalheValue}
                    onChange={e => setOutroDetalheValue(e.target.value)}
                    onBlur={() => { if (outroDetalheValue !== (task.tipo_outro_detalhe || '')) onTipoOutroDetalheChange(outroDetalheValue); }}
                    placeholder="Descreva em poucas palavras do que se trata"
                    className="w-full px-2 py-1 rounded-lg text-sm bg-transparent border border-dashed border-gray-300 dark:border-gray-600 outline-none focus:border-primary text-gray-700 dark:text-gray-200"
                  />
                ) : (
                  <span className="text-sm text-gray-600 dark:text-gray-300">{task.tipo_outro_detalhe || 'Não informado'}</span>
                )}
              </MetaRow>
            )}
            <MetaRow icon={GitBranch} label="Status">
              <select
                value={task.solicitacao_status || 'novo'}
                onChange={e => {
                  const newStatus = e.target.value as SolicitacaoStatus;
                  // "Concluído" nunca aplica direto — abre o mesmo modal de confirmação do botão
                  // "Concluir tarefa" (detalhamento + tempo + print). O select volta a mostrar o
                  // status atual sozinho, já que ele é controlado pela prop `task` e só muda de
                  // fato quando o modal chama onStatusChange no submit.
                  if (newStatus === 'concluido' && (task.solicitacao_status || 'novo') !== 'concluido') {
                    openConcludeModal();
                    return;
                  }
                  onStatusChange(newStatus);
                }}
                className={`text-xs font-bold uppercase px-2 py-1 rounded-lg border-0 outline-none cursor-pointer ${solicitacaoStatusBadge(task.solicitacao_status || 'novo')}`}
              >
                {etapasDoDepartamento(task.departamento_solicitacao).map(value => [value, rotuloEtapa(value, task.departamento_solicitacao)] as const).map(([value, label]) => (
                  <option key={value} value={value} className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 normal-case font-medium">{label}</option>
                ))}
              </select>
            </MetaRow>
            <MetaRow icon={Flag} label="Prioridade">
              <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase inline-block ${priorityColor(task.prioridade)}`}>{task.prioridade}</span>
              {ehUrgenciaAberta(task) && <span className="ml-2"><SeloUrgencia /></span>}
            </MetaRow>
            <MetaRow icon={User} label="Solicitante">
              <div className="flex items-center gap-2 flex-wrap">
                <Avatar userId={task.solicitante_id} />
                {editingSolicitante ? (
                  <select
                    autoFocus
                    value={task.solicitante_id || ''}
                    onChange={e => { onSolicitanteChange(e.target.value || null); setEditingSolicitante(false); }}
                    onBlur={() => setEditingSolicitante(false)}
                    className="text-sm font-medium px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 outline-none max-w-full"
                  >
                    <option value="">Sistema</option>
                    {users.filter(u => u.status !== 'inactive').map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{task.solicitante_id ? getResponsibleName(task.solicitante_id) : 'Sistema'}</span>
                )}
                {/* Corrige quem pediu quando o criador abriu em nome de outra pessoa — mesma
                    permissão de reclassificar Tipo/Departamento/Dependência (TAR-R17). */}
                {podeEditarTipo && !editingSolicitante && (
                  <button onClick={() => setEditingSolicitante(true)} className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary hover:underline">Editar</button>
                )}
              </div>
            </MetaRow>
            <MetaRow icon={User} label="Responsável">
              <div className="flex items-center gap-2 flex-wrap">
                <Avatar userId={task.responsavel_id} />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{getResponsibleName(task.responsavel_id)}</span>
                {!task.responsavel_id && canSelfAssignSolicitacao && (
                  <button onClick={onSelfAssign} className="text-[11px] font-semibold text-primary hover:underline">Atribuir a mim</button>
                )}
                {/* Financeiro, Faturamento e Comercial: qualquer pessoa troca o responsável (#6552). */}
                {(canDirecionarSolicitacoes || ['Financeiro', 'Faturamento', 'Comercial'].includes(task.departamento_solicitacao || '')) && (
                  <button onClick={onDirecionar} className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary hover:underline">
                    {task.responsavel_id ? 'Trocar' : 'Direcionar'}
                  </button>
                )}
              </div>
            </MetaRow>
            <MetaRow icon={Calendar} label="Criada em">
              <span className="text-sm text-gray-600 dark:text-gray-300">{formatDateTime(task.created_at)}</span>
            </MetaRow>
            <MetaRow icon={CalendarClock} label="Prazo">
              {canDirecionarSolicitacoes ? (
                // DatePicker com gatilho customizado — abre o calendário direto aqui (popover),
                // sem empilhar outro modal por cima deste.
                <DatePicker value={task.vencimento || ''} onChange={date => onReagendar(date)} className="inline-flex items-center gap-2 flex-wrap text-left">
                  <span className={`text-sm font-medium w-fit ${getDueDateBadgeStyle(task.vencimento)}`}>
                    {task.vencimento ? parseLocalDate(task.vencimento).toLocaleDateString('pt-BR') : 'Sem prazo definido'}
                  </span>
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary hover:underline">Reagendar</span>
                </DatePicker>
              ) : (
                <span className={`text-sm font-medium w-fit ${getDueDateBadgeStyle(task.vencimento)}`}>
                  {task.vencimento ? parseLocalDate(task.vencimento).toLocaleDateString('pt-BR') : 'Sem prazo definido'}
                </span>
              )}
              <PrazoBadge tarefa={task} className="ml-2" />
              <SeloVisualizado tarefa={task} className="ml-2" />
            </MetaRow>
            {fluxoTI && (task.solicitacao_status === 'concluido' || dificuldadeAtual) && (
              <MetaRow icon={TrendingUp} label="Dificuldade">
                {podeMudarDificuldade ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={dificuldadePendente ?? dificuldadeAtual ?? ''}
                      onChange={e => setDificuldadePendente(e.target.value)}
                      disabled={salvandoDificuldade}
                      aria-label="Dificuldade"
                      className="text-sm font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-60"
                    >
                      {!dificuldadeAtual && <option value="">Não informada</option>}
                      {DIFICULDADES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                    {dificuldadePendente && dificuldadePendente !== (dificuldadeAtual ?? '') && (
                      <>
                        <button type="button" onClick={() => mudarDificuldade(dificuldadePendente)} disabled={salvandoDificuldade}
                          className="px-2.5 py-1 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                          {salvandoDificuldade ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button type="button" onClick={() => setDificuldadePendente(null)} disabled={salvandoDificuldade}
                          className="px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                          Cancelar
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-600 dark:text-gray-300">{rotuloDificuldade(dificuldadeAtual)}</span>
                )}
              </MetaRow>
            )}
            {subtarefas.length > 0 && (
              <MetaRow icon={TrendingUp} label="Progresso">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden max-w-[180px]">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">{subtarefasDone}/{subtarefas.length}</span>
                </div>
              </MetaRow>
            )}
          </div>

          {/* Cronômetro — só no fluxo da TI; as outras equipes trabalham por tarefa, sem contar horas (#6518) */}
          {fluxoTI && (
          <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock size={16} className="text-gray-400" /> Cronômetro
              </h4>
              {taskTimeEntries.length > 0 && (
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Total: {formatDuration(totalTrackedSeconds)}</span>
              )}
            </div>

            {!task.responsavel_id ? (
              <p className="text-xs text-gray-400">Atribua um responsável para habilitar o cronômetro.</p>
            ) : task.responsavel_id === currentUserId ? (
              isRunningHere ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-2xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{formatDuration(timerElapsedSeconds)}</span>
                  <button onClick={onPauseTimer} disabled={timerBusy} className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    <Pause size={14} /> Pausar
                  </button>
                  <button onClick={onFinishTimer} disabled={timerBusy} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                    <Square size={14} /> Finalizar
                  </button>
                </div>
              ) : openTimeEntry ? (
                <p className="text-xs text-gray-400">Você tem um cronômetro rodando em outra tarefa. Pause ou finalize antes de iniciar este.</p>
              ) : (
                <button onClick={onStartTimer} disabled={timerBusy} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                  <Play size={14} /> Iniciar
                </button>
              )
            ) : (
              <p className="text-xs text-gray-400">Só {getResponsibleName(task.responsavel_id)} pode controlar o cronômetro desta solicitação.</p>
            )}

            {/* Tempo manual — o responsável pode registrar uma estimativa mesmo sem ter rodado o cronômetro */}
            {task.responsavel_id === currentUserId && (
              addingManualTime ? (
                <div className="mt-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" min={0} placeholder="Horas" value={manualHours} onChange={e => setManualHours(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
                    <input type="number" min={0} max={59} placeholder="Minutos" value={manualMinutes} onChange={e => setManualMinutes(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
                    <DatePicker value={manualDate} onChange={setManualDate} placeholder="Data" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setAddingManualTime(false); setManualHours(''); setManualMinutes(''); }} className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
                    <button
                      onClick={handleSubmitManualTime}
                      disabled={savingManualTime || ((parseInt(manualHours, 10) || 0) * 60 + (parseInt(manualMinutes, 10) || 0)) <= 0}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50 flex items-center gap-1"
                    >
                      {savingManualTime ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingManualTime(true)} className="mt-2 text-[11px] font-semibold text-primary hover:underline flex items-center gap-1">
                  <Plus size={11} /> Adicionar tempo manualmente
                </button>
              )
            )}

            {taskTimeEntries.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1.5 max-h-28 overflow-y-auto">
                {taskTimeEntries.map(entry => (
                  editingEntryId === entry.id ? (
                    <div key={entry.id} className="flex items-center gap-1.5 text-xs">
                      <input type="number" min={0} value={editEntryHours} onChange={e => setEditEntryHours(e.target.value)} className="w-12 px-1.5 py-1 border border-gray-200 dark:border-gray-700 rounded text-xs bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
                      <span className="text-gray-400">h</span>
                      <input type="number" min={0} max={59} value={editEntryMinutes} onChange={e => setEditEntryMinutes(e.target.value)} className="w-12 px-1.5 py-1 border border-gray-200 dark:border-gray-700 rounded text-xs bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
                      <span className="text-gray-400">min</span>
                      <button onClick={handleSubmitEntryEdit} disabled={savingEntryEdit} className="p-1 text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50">
                        {savingEntryEdit ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      </button>
                      <button onClick={() => setEditingEntryId(null)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div key={entry.id} className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 gap-2 group">
                      <span className="truncate flex items-center gap-1.5">
                        {/* Entrada manual só tem data (a hora é um valor fixo interno, sem sentido pra mostrar) */}
                        {entry.user?.full_name || getResponsibleName(entry.user_id)} — {entry.is_manual ? formatDate(entry.started_at) : formatDateTime(entry.started_at)}
                        {entry.is_manual && (
                          <span className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[9px] font-bold uppercase shrink-0">manual</span>
                        )}
                        {/* Encerrado pelo cron por esquecimento: o tempo é estimativa, não medição — precisa aparecer como tal */}
                        {entry.encerrado_automaticamente && (
                          <span title="Encerrado automaticamente porque o cronômetro ficou rodando sem sinal de atividade. O tempo é uma estimativa — ajuste se precisar." className="px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[9px] font-bold uppercase shrink-0">auto</span>
                        )}
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="font-mono font-semibold">{entry.duration_seconds != null ? formatDuration(entry.duration_seconds) : 'em andamento'}</span>
                        {task.responsavel_id === currentUserId && entry.duration_seconds != null && (
                          <button onClick={() => startEditEntry(entry)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-primary transition-opacity">
                            <Edit2 size={11} />
                          </button>
                        )}
                      </span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
          )}

          {/* Descrição */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-between">
              <span>Descrição</span>
              {isCreatorOrAdmin && !editingDesc && (
                <button onClick={() => { setDescValue(task.descricao || ''); setEditingDesc(true); }} className="p-1 text-gray-400 hover:text-primary transition-colors">
                  <Edit2 size={14} />
                </button>
              )}
            </h4>
            {editingDesc ? (
              <div className="space-y-2">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-0 bg-gray-50 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-700 px-1">
                    <button type="button" onClick={() => setDescPreview('edit')} className={`px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${descPreview === 'edit' ? 'text-gray-800 dark:text-white bg-white dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400'}`}>
                      <Edit2 size={12} /> Editar
                    </button>
                    <button type="button" onClick={() => setDescPreview('preview')} className={`px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${descPreview === 'preview' ? 'text-gray-800 dark:text-white bg-white dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400'}`}>
                      <Eye size={12} /> Preview
                    </button>
                  </div>
                  {descPreview === 'edit' ? (
                    <MentionTextarea
                      users={users}
                      priorityUserIds={priorityUserIds}
                      value={descValue}
                      onChange={e => setDescValue(e.target.value)}
                      onMention={() => { }}
                      className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-0 text-xs text-gray-900 dark:text-slate-200 focus:outline-none focus:ring-0 resize-y min-h-[150px] font-mono leading-relaxed"
                      placeholder="Descrição da solicitação... (suporta **markdown**)"
                    />
                  ) : (
                    <div className="min-h-[150px] px-4 py-3 bg-white dark:bg-gray-800 overflow-y-auto max-h-[300px] text-sm text-gray-600 dark:text-gray-300">
                      {descValue.trim() ? <TaskDescriptionMarkdown value={descValue} variant="compact" /> : <p className="text-gray-400 italic text-xs">Nada para visualizar ainda...</p>}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingDesc(false)} className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-slate-700 rounded-xl transition-colors">Cancelar</button>
                  <button onClick={handleSaveDesc} disabled={savingDesc} className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl flex items-center gap-1 shadow-lg shadow-primary/20">
                    {savingDesc ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                  </button>
                </div>
              </div>
            ) : task.descricao ? (
              <div className="text-sm bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-700 p-4 text-gray-600 dark:text-gray-300">
                <TaskDescriptionMarkdown value={task.descricao} variant="full" />
              </div>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 text-sm italic">Sem descrição.</p>
            )}
          </div>
          {/* Checklist — pedidos das equipes que trabalham por tarefa (#6518). Grava direto na solicitação;
              o estado local evita esperar o recarregamento para marcar vários itens seguidos. */}
          {!fluxoTI && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <ListTree size={14} className="text-gray-400" /> Checklist
                {checklist.length > 0 && <span className="text-xs font-semibold text-gray-400">{checklist.filter(c => c.completed).length}/{checklist.length}</span>}
              </h4>
              <div className="space-y-1.5 mb-2">
                {checklist.map(item => (
                  <div key={item.id} className="flex items-center gap-2 group px-3 py-2 bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 rounded-lg text-sm">
                    <input
                      type="checkbox"
                      checked={item.completed}
                      disabled={!podeEditarChecklist}
                      onChange={e => salvarChecklist(checklist.map(c => c.id === item.id ? { ...c, completed: e.target.checked } : c))}
                      aria-label={`Marcar ${item.text}`}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className={`flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}>{item.text}</span>
                    {podeEditarChecklist && (
                      <button type="button" onClick={() => salvarChecklist(checklist.filter(c => c.id !== item.id))} aria-label={`Remover ${item.text}`}
                        className="p-1 text-gray-400 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 focus:opacity-100">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {checklist.length === 0 && <p className="text-xs text-gray-400 italic">Nenhum item ainda.</p>}
              </div>
              {podeEditarChecklist && (
                <div className="flex gap-2">
                  <input
                    type="text" value={novoItemChecklist} onChange={e => setNovoItemChecklist(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && novoItemChecklist.trim()) { e.preventDefault(); salvarChecklist([...checklist, { id: Date.now().toString(), text: novoItemChecklist.trim(), completed: false }]); setNovoItemChecklist(''); } }}
                    placeholder="Adicionar item..." aria-label="Novo item da checklist"
                    className="flex-1 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <button type="button" disabled={!novoItemChecklist.trim()}
                    onClick={() => { salvarChecklist([...checklist, { id: Date.now().toString(), text: novoItemChecklist.trim(), completed: false }]); setNovoItemChecklist(''); }}
                    className="px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm font-semibold disabled:opacity-50">
                    <Plus size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Anexos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Paperclip size={16} className="text-gray-400" /> Anexos
              </h4>
              <label className={`flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline ${uploadingFile ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
                {uploadingFile ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                Adicionar
                <input type="file" className="hidden" onChange={handleFileSelected} disabled={uploadingFile} />
              </label>
            </div>
            {task.anexos_url && task.anexos_url.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {task.anexos_url.map((url, i) => {
                  const { icon: Icon, bg, text } = getFileIcon(url);
                  const ext = (url.split('.').pop() || '').split('?')[0].toUpperCase();
                  return (
                    <div key={i} className="group relative border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex items-center gap-2.5 hover:shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${bg}`}><Icon size={16} className={text} /></div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate" title={getAnexoDisplayName(url)}>{getAnexoDisplayName(url)}</p>
                          <p className="text-[10px] text-gray-400 uppercase">{ext}</p>
                        </div>
                      </a>
                      {isCreatorOrAdmin && (
                        <button onClick={() => onRemoveAttachment(url)} title="Remover" className="opacity-0 group-hover:opacity-100 absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-400 hover:text-red-500 transition-opacity shadow-sm">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">Nenhum anexo ainda.</p>
            )}
          </div>

        </div>

          {/* Rail lateral — Subtarefas / Comentários / Atividades / Depende de, com scroll próprio
              em telas grandes. As 4 abas usam texto/ícone/padding reduzidos pra caber sem cortar —
              overflow-x-auto fica só como rede de segurança pra viewports bem estreitos. */}
          <div className="w-full lg:w-[440px] shrink-0 border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/20 flex flex-col min-h-0 lg:h-full">
            <div className="flex items-center px-1.5 pt-3 border-b border-gray-100 dark:border-gray-700 shrink-0 overflow-x-auto">
              {(!task.parent_id) && (
                <button onClick={() => setActiveTab('subtarefas')} className={`px-1.5 py-2 text-xs font-bold flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'subtarefas' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300'}`}>
                  <ListTree size={12} /> Subtarefas
                  {subtarefas.length > 0 && <span className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{subtarefas.length}</span>}
                </button>
              )}
              <button onClick={() => setActiveTab('comentarios')} className={`px-1.5 py-2 text-xs font-bold flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'comentarios' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300'}`}>
                <MessageSquare size={12} /> Comentários
                {comments.length > 0 && <span className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{comments.length}</span>}
              </button>
              <button onClick={() => setActiveTab('atividades')} className={`px-1.5 py-2 text-xs font-bold flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'atividades' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300'}`}>
                <Activity size={12} /> Atividades
              </button>
              <button onClick={() => setActiveTab('dependencia')} className={`px-1.5 py-2 text-xs font-bold flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'dependencia' ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300'}`}>
                <GitBranch size={12} /> Depende de
                {aindaBloqueando && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" title="Ainda bloqueada" />}
              </button>
            </div>

            {activeTab === 'subtarefas' && !task.parent_id && (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {subtarefas.map(sub => (
                    <div key={sub.id} onClick={() => onOpenSubtask(sub)} className="flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0 ${solicitacaoStatusBadge(sub.solicitacao_status || 'novo')}`}>{rotuloEtapa(sub.solicitacao_status, task.departamento_solicitacao)}</span>
                        <span className="truncate text-gray-700 dark:text-gray-200">{sub.titulo}</span>
                        {openTimeEntry && String(openTimeEntry.tarefa_id) === String(sub.id) && <Play size={11} className="text-emerald-500 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Avatar userId={sub.responsavel_id} size={18} />
                        <span className="text-xs text-gray-400">{getResponsibleName(sub.responsavel_id)}</span>
                      </div>
                    </div>
                  ))}
                  {subtarefas.length === 0 && !addingSubtask && (
                    <p className="text-xs text-gray-400 italic">Nenhuma subtarefa ainda.</p>
                  )}
                </div>

                {addingSubtask ? (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
                    <input
                      type="text" placeholder="Título da subtarefa" value={newSubtask.titulo}
                      onChange={e => setNewSubtask({ ...newSubtask, titulo: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select value={newSubtask.responsavel_id} onChange={e => setNewSubtask({ ...newSubtask, responsavel_id: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20">
                        <option value="">Sem responsável</option>
                        {users.filter(u => u.status !== 'inactive').map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                      </select>
                      <select value={newSubtask.prioridade} onChange={e => setNewSubtask({ ...newSubtask, prioridade: e.target.value as TarefaPrioridade })} className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20">
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                      </select>
                    </div>
                    <DatePicker value={newSubtask.vencimento} onChange={v => setNewSubtask({ ...newSubtask, vencimento: v })} placeholder="Vencimento (opcional)" />
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setAddingSubtask(false)} className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
                      <button onClick={handleSubmitSubtask} disabled={!newSubtask.titulo.trim() || savingSubtask} className="px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50 flex items-center gap-1">
                        {savingSubtask ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar
                      </button>
                    </div>
                  </div>
                ) : canDirecionarSolicitacoes && (
                  <button onClick={() => setAddingSubtask(true)} className="mt-3 text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                    <Plus size={12} /> Adicionar subtarefa
                  </button>
                )}
              </div>
            )}

            {activeTab === 'comentarios' && (
              <div className="flex-1 flex flex-col min-h-0 p-4">
                <div className="flex-1 space-y-4 mb-4 overflow-y-auto pr-1">
                  {comments.length === 0 ? (
                    <p className="text-center text-gray-400 text-xs py-6">Nenhum comentário ainda.</p>
                  ) : comments.map(comment => (
                    <div key={comment.id} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${corDoUsuario(comment.user_id).nome}`}>{comment.user?.full_name || 'Usuário'}</span>
                          <span className="text-[10px] text-gray-400 dark:text-slate-500">{formatDateTime(comment.created_at)}</span>
                        </div>
                        {comment.user_id === currentUserId && editingCommentId !== comment.id && !comentarioApagado(comment) && confirmandoApagarId !== comment.id && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-all">
                            <button onClick={() => { setEditCommentValue(comment.conteudo); setEditingCommentId(comment.id); }} title="Editar comentário" aria-label="Editar comentário" className="p-0.5 text-gray-400 hover:text-primary">
                              <Edit2 size={12} />
                            </button>
                            <button onClick={() => setConfirmandoApagarId(comment.id)} title="Apagar comentário" aria-label="Apagar comentário" className="p-0.5 text-gray-400 hover:text-red-500">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        )}
                        {confirmandoApagarId === comment.id && (
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-gray-500 dark:text-slate-400">Apagar este comentário?</span>
                            <button onClick={() => setConfirmandoApagarId(null)} disabled={apagandoComentario} className="px-2 py-0.5 font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Cancelar</button>
                            <button onClick={() => handleApagarComentario(comment.id)} disabled={apagandoComentario} className="px-2 py-0.5 font-semibold text-white bg-red-500 hover:bg-red-600 rounded flex items-center gap-1">
                              {apagandoComentario ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Apagar
                            </button>
                          </div>
                        )}
                      </div>
                      {comentarioApagado(comment) ? (
                        <div className="p-3 rounded-lg rounded-tl-none border border-dashed border-gray-200 dark:border-gray-700 text-xs italic text-gray-400 dark:text-slate-500">
                          {TEXTO_COMENTARIO_APAGADO}
                        </div>
                      ) : editingCommentId === comment.id ? (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg rounded-tl-none border border-primary/30 shadow-sm space-y-2">
                          <MentionTextarea users={users} priorityUserIds={priorityUserIds} value={editCommentValue} onChange={e => setEditCommentValue(e.target.value)} onMention={() => { }} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-primary/50 rounded-lg text-sm dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y min-h-[70px]" />
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditingCommentId(null)} className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg">Cancelar</button>
                            <button onClick={handleSubmitCommentEdit} disabled={savingComment} className="px-3 py-1.5 text-[11px] font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg flex items-center gap-1">
                              {savingComment ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Salvar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg rounded-tl-none border border-gray-100 dark:border-gray-700 border-l-4 ${corDoUsuario(comment.user_id).barra} text-sm text-gray-600 dark:text-gray-300 space-y-2`}>
                          {comment.conteudo && <ComentarioTexto texto={comment.conteudo} usuarios={users} />}
                          {comment.anexos_url && comment.anexos_url.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {comment.anexos_url.map((url, i) => {
                                if (ehImagemUrl(url)) return (
                                  <a key={url + i} href={url} target="_blank" rel="noopener noreferrer" title="Abrir imagem em nova aba">
                                    <img src={url} alt={`Imagem ${i + 1} do comentário`} className="h-24 w-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity" />
                                  </a>
                                );
                                const { icon: Icon, bg, text } = getFileIcon(url);
                                const ext = (url.split('.').pop() || '').split('?')[0].toUpperCase();
                                return (
                                  <a key={url + i} href={url} target="_blank" rel="noopener noreferrer" title={`Abrir ${getAnexoDisplayName(url)}`}
                                    className="flex items-center gap-2.5 max-w-[16rem] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-2.5 hover:shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all">
                                    <div className={`p-2 rounded-lg shrink-0 ${bg}`}><Icon size={16} className={text} /></div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{getAnexoDisplayName(url)}</p>
                                      <p className="text-[10px] text-gray-400 uppercase">{ext || 'arquivo'}</p>
                                    </div>
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <form onSubmit={handleSubmitComment} className="shrink-0 space-y-2">
                  {commentImages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {commentImages.map((img, i) => (
                        <div key={img.preview || `${img.file.name}-${i}`} className="relative group/img">
                          {img.preview ? (
                            <img src={img.preview} alt={`Imagem ${i + 1} a enviar`} className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700" />
                          ) : (() => {
                            const { icon: Icon, bg, text } = getFileIcon(img.file.name);
                            return (
                              <div className="h-16 max-w-[12rem] flex items-center gap-2 px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <div className={`p-1.5 rounded-lg shrink-0 ${bg}`}><Icon size={14} className={text} /></div>
                                <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 truncate" title={img.file.name}>{img.file.name}</p>
                              </div>
                            );
                          })()}
                          <button
                            type="button" onClick={() => removeCommentImage(i)} title="Remover arquivo"
                            className="absolute -top-1.5 -right-1.5 p-0.5 bg-white dark:bg-gray-800 text-gray-400 hover:text-error border border-gray-200 dark:border-gray-700 rounded-full shadow-sm"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <MentionTextarea
                      users={users} priorityUserIds={priorityUserIds} value={newComment} onChange={e => setNewComment(e.target.value)}
                      onMention={userId => setCommentMentions(prev => new Set(prev).add(userId))}
                      placeholder="Escreva um comentário... (suporta **markdown**, @ para mencionar, Ctrl+V para colar imagem ou arquivo)"
                      className="w-full pl-4 pr-16 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none min-h-[50px]"
                      onPaste={handleCommentPaste}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(e); } }}
                    />
                    <label title="Anexar arquivo (imagem, PDF, planilha, documento, vídeo...)" aria-label="Anexar arquivo" className="absolute right-9 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors cursor-pointer">
                      <Paperclip size={17} />
                      <input type="file" multiple className="hidden" onChange={handleCommentImageInput} />
                    </label>
                    <button type="submit" disabled={(!newComment.trim() && commentImages.length === 0) || sendingComment} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50">
                      {sendingComment ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                  {commentAnexoErro && <p role="alert" className="text-[11px] font-semibold text-error">{commentAnexoErro}</p>}
                  {commentImages.length > 0 && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">Os anexos ficam acessíveis para quem tiver o link. Evite documentos com dados pessoais de clientes.</p>
                  )}
                </form>
              </div>
            )}

            {activeTab === 'atividades' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 mt-1.5 shrink-0" />
                  <div className="text-xs">
                    <p className="text-gray-700 dark:text-gray-200"><strong>{task.solicitante_id ? getResponsibleName(task.solicitante_id) : 'Sistema'}</strong> criou a solicitação</p>
                    <p className="text-gray-400 mt-0.5">{formatDateTime(task.created_at)}</p>
                  </div>
                </div>
                {activityEvents.map(ev => (
                  <div key={ev.id} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary/40 mt-1.5 shrink-0" />
                    <div className="text-xs">
                      <p className="text-gray-700 dark:text-gray-200">
                        <strong>{ev.user?.full_name || getResponsibleName(ev.user_id)}</strong> mudou o status
                        {ev.from_status && <> de <span className="font-medium">{rotuloEtapa(ev.from_status, task.departamento_solicitacao)}</span></>}
                        {' '}para <span className="font-medium">{rotuloEtapa(ev.to_status, task.departamento_solicitacao)}</span>
                      </p>
                      <p className="text-gray-400 mt-0.5">{formatDateTime(ev.created_at)}</p>
                    </div>
                  </div>
                ))}
                {eventosDependencia.map(ev => {
                  const pessoa = (id: string | null) => (id ? getResponsibleName(id) : null);
                  const descrever = (id: string | null, texto: string | null) => [pessoa(id), texto].filter(Boolean).join(' — ') || 'nada';
                  return (
                    <div key={`dep-${ev.id}`} className="flex gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ev.acao === 'resolveu' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      <div className="text-xs">
                        <p className="text-gray-700 dark:text-gray-200">
                          <strong>{ev.user_id ? getResponsibleName(ev.user_id) : 'Sistema'}</strong>{' '}
                          {ev.acao === 'adicionou' && <>passou a depender de <span className="font-medium">{descrever(ev.pessoa_depois, ev.texto_depois)}</span></>}
                          {ev.acao === 'resolveu' && <>resolveu a dependência de <span className="font-medium">{descrever(ev.pessoa_antes, ev.texto_antes)}</span></>}
                          {ev.acao === 'trocou' && <>trocou a dependência de <span className="font-medium">{descrever(ev.pessoa_antes, ev.texto_antes)}</span> para <span className="font-medium">{descrever(ev.pessoa_depois, ev.texto_depois)}</span></>}
                        </p>
                        <p className="text-gray-400 mt-0.5">{formatDateTime(ev.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                {activityEvents.length === 0 && eventosDependencia.length === 0 && (
                  <p className="text-xs text-gray-400 italic">Nenhuma mudança registrada ainda.</p>
                )}
              </div>
            )}

            {activeTab === 'dependencia' && (
              <div className="flex-1 overflow-y-auto p-4">
                {aindaBloqueando ? (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-lg text-sm">
                    <Flag size={14} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                    <div className="min-w-0">
                      {task.bloqueada_por_pessoa_id && (
                        <div className="flex items-center gap-2 mb-1">
                          <Avatar userId={task.bloqueada_por_pessoa_id} size={18} />
                          <span className="font-semibold text-amber-700 dark:text-amber-400">Aguardando {getResponsibleName(task.bloqueada_por_pessoa_id)}</span>
                        </div>
                      )}
                      {task.bloqueada_por_texto && (
                        <span className="text-amber-700 dark:text-amber-400">{task.bloqueada_por_texto}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Esta solicitação não depende de nada.</p>
                )}

                {podeMexerDependencia && aindaBloqueando && (
                  <button
                    type="button"
                    onClick={() => { setBloqueadaPorTextoValue(''); onDefinirDependencia(null, null); }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-success hover:bg-success/90 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-success/40"
                  >
                    <CheckCircle2 size={15} /> Resolver dependência
                  </button>
                )}
                {podeMexerDependencia && !aindaBloqueando && ultimaResolvida && (
                  <button
                    type="button"
                    onClick={() => { setBloqueadaPorTextoValue(ultimaResolvida.texto_antes ?? ''); onDefinirDependencia(ultimaResolvida.pessoa_antes, ultimaResolvida.texto_antes); }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                    title="Recoloca a última dependência resolvida"
                  >
                    <Flag size={15} /> Voltar a depender de {[ultimaResolvida.pessoa_antes ? getResponsibleName(ultimaResolvida.pessoa_antes) : null, ultimaResolvida.texto_antes].filter(Boolean).join(' — ')}
                  </button>
                )}

                {podeMexerDependencia && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Aguardando quem?</label>
                      <select
                        value={task.bloqueada_por_pessoa_id ?? ''}
                        onChange={e => onBloqueadaPorPessoaChange(e.target.value || null)}
                        className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="">Ninguém</option>
                        {users.filter(u => u.status !== 'inactive').map(u => (
                          <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Motivo</label>
                      <input
                        type="text"
                        maxLength={200}
                        value={bloqueadaPorTextoValue}
                        onChange={e => setBloqueadaPorTextoValue(e.target.value)}
                        onBlur={() => { if (bloqueadaPorTextoValue !== (task.bloqueada_por_texto || '')) onBloqueadaPorTextoChange(bloqueadaPorTextoValue); }}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        placeholder="Ex.: aguardando aprovação do financeiro"
                        className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Concluir tarefa — pede detalhamento (obrigatório), confirmação do tempo gasto e print
          (opcional, com suporte a colar da área de transferência) antes de mudar o status pra
          "concluido". */}
      {showConcludeModal && (
        <div
          role="dialog" aria-modal="true" aria-labelledby="concluir-titulo"
          onKeyDown={e => { if (e.key === 'Escape' && !savingConclusao) { e.stopPropagation(); closeConcludeModal(); } }}
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={e => { e.stopPropagation(); closeConcludeModal(); }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={20} className="text-success" />
              <h3 id="concluir-titulo" className="text-lg font-bold text-gray-900 dark:text-white">Concluir tarefa</h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Descreva o que foi feito{fluxoTI ? ' e confirme o tempo gasto' : ''} para marcar "{task.titulo}" como concluída.</p>

            <label htmlFor="concluir-detalhe" className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
              O que foi feito? <span className="text-error">*</span>
            </label>
            <textarea
              id="concluir-detalhe"
              autoFocus
              value={conclusaoDetalhe}
              onChange={e => setConclusaoDetalhe(e.target.value)}
              onPaste={handleConclusaoPaste}
              placeholder="Detalhe o que foi feito para concluir a solicitação..."
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y min-h-[100px]"
            />

            {pedeDificuldade && (
              <fieldset className="mt-4">
                <legend className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                  Qual foi a dificuldade? <span className="text-error">*</span>
                </legend>
                {/* Rádios nativos (escondidos): Tab entra no grupo e as setas trocam a opção, que é o que
                    leitor de tela e teclado esperam. Botões com role="radio" só respondiam ao Tab. */}
                <div className="grid grid-cols-3 gap-2">
                  {DIFICULDADES.map(d => (
                    <label key={d.value} className="cursor-pointer">
                      <input
                        type="radio" name="dificuldade-conclusao" value={d.value} required
                        checked={conclusaoDificuldade === d.value}
                        onChange={() => setConclusaoDificuldade(d.value)}
                        className="peer sr-only"
                      />
                      <span className={`block text-center px-3 py-2 rounded-lg text-sm font-semibold border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 ${conclusaoDificuldade === d.value ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary/50'}`}>
                        {d.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {fluxoTI && (<>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 mt-4 mb-1.5">
              <Clock size={13} className="text-gray-400" /> Tempo gasto
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <input
                  aria-label="Horas gastas"
                  type="number" min={0} value={conclusaoHoras}
                  onChange={e => setConclusaoHoras(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 pr-10 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">h</span>
              </div>
              <div className="relative">
                <input
                  aria-label="Minutos gastos"
                  type="number" min={0} max={59} value={conclusaoMinutos}
                  onChange={e => setConclusaoMinutos(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 pr-12 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">min</span>
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {totalTrackedSeconds > 0
                ? `Cronômetro registrou ${formatDuration(totalTrackedSeconds)}. Ajuste se o tempo real foi outro.`
                : 'O cronômetro não foi usado nesta tarefa. Informe quanto tempo foi gasto de fato.'}
            </p>
            </>)}

            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mt-4 mb-1.5">
              Print <span className="text-gray-400 font-normal normal-case">(opcional)</span>
            </label>
            {conclusaoPrintPreview ? (
              <div className="relative inline-block">
                <img src={conclusaoPrintPreview} alt="Print anexado" className="max-h-40 rounded-lg border border-gray-200 dark:border-gray-700" />
                <button onClick={() => setConclusaoFile(null)} title="Remover" className="absolute -top-2 -right-2 p-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-red-500 shadow-sm">
                  <X size={12} />
                </button>
              </div>
            ) : (
              <label
                onPaste={handleConclusaoPaste}
                tabIndex={0}
                className="flex flex-col items-center justify-center gap-1.5 px-3 py-5 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-center cursor-pointer hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <ClipboardPaste size={18} className="text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Cole uma imagem (<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">Ctrl+V</kbd>) ou <span className="text-primary font-semibold">selecione um arquivo</span>
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={handleConclusaoFileInput} />
              </label>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeConcludeModal} disabled={savingConclusao} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button
                onClick={handleSubmitConclusao}
                disabled={!conclusaoDetalhe.trim() || (pedeDificuldade && !conclusaoDificuldade) || savingConclusao}
                className="px-4 py-2 text-sm font-semibold text-white bg-success hover:bg-success/90 rounded-xl disabled:opacity-50 flex items-center gap-1.5 shadow-lg shadow-success/20"
              >
                {savingConclusao ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Concluir tarefa
              </button>
            </div>
            {/* O botão desabilitado sozinho não diz o que falta. */}
            {(!conclusaoDetalhe.trim() || (pedeDificuldade && !conclusaoDificuldade)) && !savingConclusao && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right mt-2">
                Para concluir, falta: {[!conclusaoDetalhe.trim() && 'o que foi feito', pedeDificuldade && !conclusaoDificuldade && 'a dificuldade'].filter(Boolean).join(' e ')}.
              </p>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default SolicitacaoDetailModal;
